import { useCallback } from 'react';
import type { ContentPack, BuildState, Palette } from '../../packs/types';
import { renderBuild } from '../render';
import { applyBorder } from '../../pixel/border';
import { renderAns } from '../../exporters/ans';
import { slugify } from '../share';

export function useExport(
  offRef: React.RefObject<HTMLCanvasElement | null>,
  pack: ContentPack,
  build: BuildState,
  palette: Palette,
  background: string,
  borderEnabled: boolean,
  borderColor: string
) {
  const exportPng = useCallback((size: 64 | 128 = 64) => {
    const off = offRef.current;
    if (!off) return;
    const octx = off.getContext('2d', { willReadFrequently: true });
    if (!octx) return;
    octx.imageSmoothingEnabled = false;
    renderBuild(octx, build, pack, palette, 0);
    if (borderEnabled) applyBorder(octx, borderColor);
    const out = document.createElement('canvas');
    out.width = size; out.height = size;
    const oc = out.getContext('2d');
    if (!oc) return;
    oc.imageSmoothingEnabled = false;
    oc.drawImage(off, 0, 0, size, size);
    const url = out.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pack.metadata?.filePrefix ?? 'us'}-${slugify(build.name)}_${size}.png`;
    a.click();
  }, [offRef, build, pack, palette, borderEnabled, borderColor]);

  const exportAns = useCallback((format: 'truecolor' | 'xterm256', variant: '64' | '120' | '128') => {
    const off = offRef.current;
    if (!off) return;
    const ctx = off.getContext('2d', { willReadFrequently: true })!;
    ctx.imageSmoothingEnabled = false;
    renderBuild(ctx, build, pack, palette, 0);
    if (borderEnabled) applyBorder(ctx, borderColor);
    const imageData = ctx.getImageData(0, 0, pack.rasterSize, pack.rasterSize);
    const withBg = variant !== '120';
    const content = renderAns(imageData, {
      include: { background: withBg, border: borderEnabled, caption: null },
      format,
      scale: variant === '128' ? 2 : 1,
      targetCols: variant === '120' ? 120 : undefined,
    }, { backgroundColor: background, borderColor: borderEnabled ? borderColor : undefined });
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pack.metadata?.filePrefix ?? 'us'}-${slugify(build.name)}_${format === 'truecolor' ? 'tc' : '256'}_${variant}.ans`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [offRef, build, pack, palette, background, borderEnabled, borderColor]);

  const exportSvg = useCallback(() => {
    const off = offRef.current;
    if (!off) return;
    const ctx = off.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.clearRect(0, 0, pack.rasterSize, pack.rasterSize);
    ctx.imageSmoothingEnabled = false;
    renderBuild(ctx, build, pack, palette, 0);
    if (borderEnabled) applyBorder(ctx, borderColor);
    const { data } = ctx.getImageData(0, 0, pack.rasterSize, pack.rasterSize);
    const size = pack.rasterSize;
    const rects: string[] = [];
    // Run-length encode horizontally: contiguous same-color pixels in a row
    // collapse into a single wider <rect>. Cuts SVG file size by 3-5x on
    // typical sprites without changing the rendered output.
    const hex = (i: number) =>
      `#${data[i].toString(16).padStart(2, '0')}${data[i + 1].toString(16).padStart(2, '0')}${data[i + 2].toString(16).padStart(2, '0')}`;
    for (let y = 0; y < size; y++) {
      let x = 0;
      while (x < size) {
        const i = (y * size + x) * 4;
        const a = data[i + 3];
        if (a === 0) { x++; continue; }
        const r = data[i], g = data[i + 1], b = data[i + 2];
        let runLen = 1;
        while (x + runLen < size) {
          const j = (y * size + x + runLen) * 4;
          if (data[j] !== r || data[j + 1] !== g || data[j + 2] !== b || data[j + 3] !== a) break;
          runLen++;
        }
        const opacity = a < 255 ? ` opacity="${(a / 255).toFixed(3)}"` : '';
        rects.push(`<rect x="${x}" y="${y}" width="${runLen}" height="1" fill="${hex(i)}"${opacity}/>`);
        x += runLen;
      }
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size * 4}" height="${size * 4}" shape-rendering="crispEdges">\n${rects.join('\n')}\n</svg>`;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pack.metadata?.filePrefix ?? 'us'}-${slugify(build.name)}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [offRef, build, pack, palette, borderEnabled, borderColor]);

  const getPngDataUrl = useCallback((size: 64 | 128 = 128) => {
    const off = offRef.current;
    if (!off) return null;
    const octx = off.getContext('2d', { willReadFrequently: true });
    if (!octx) return null;
    octx.imageSmoothingEnabled = false;
    renderBuild(octx, build, pack, palette, 0);
    if (borderEnabled) applyBorder(octx, borderColor);
    const out = document.createElement('canvas');
    out.width = size; out.height = size;
    const oc = out.getContext('2d');
    if (!oc) return null;
    oc.imageSmoothingEnabled = false;
    oc.drawImage(off, 0, 0, size, size);
    return out.toDataURL('image/png');
  }, [offRef, build, pack, palette, borderEnabled, borderColor]);

  return { exportPng, exportAns, exportSvg, getPngDataUrl };
}
